import { warehouseCaseApi, } from '@sellsen/generated-sources';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
const QUERY_KEYS = {
    all: ['warehouse-cases'],
    categories: () => [...QUERY_KEYS.all, 'categories'],
    list: (params) => [...QUERY_KEYS.all, 'list', params],
    byPackage: (warehousePackageId) => [...QUERY_KEYS.all, 'by-package', warehousePackageId],
    messages: (caseNumber) => [...QUERY_KEYS.all, 'messages', caseNumber],
    unreadCount: () => [...QUERY_KEYS.all, 'unread-count'],
    counts: (params) => [...QUERY_KEYS.all, 'counts', params],
};
/** Rozet, liste ve açık sohbet için yenileme aralıkları (ms). Push kanalı yok, polling var. */
const REFETCH_INTERVAL = {
    unreadCount: 60_000,
    list: 30_000,
    messages: 15_000,
};
/** Kategoriler backend'de de cache'li; burada gereksiz istek atmamak için uzun staleTime. */
export const useWarehouseCaseCategoriesQuery = (enabled = true) => {
    return useQuery({
        queryKey: QUERY_KEYS.categories(),
        queryFn: () => warehouseCaseApi.listCaseCategories(),
        staleTime: 30 * 60 * 1000,
        enabled,
    });
};
export const useWarehouseCasesQuery = (params, enabled = true) => {
    return useQuery({
        queryKey: QUERY_KEYS.list(params),
        queryFn: () => warehouseCaseApi.listCases(params),
        refetchInterval: REFETCH_INTERVAL.list,
        enabled,
    });
};
/** Hızlı filtre sayaçları; liste ile aynı aralıkta tazelenir. */
export const useWarehouseCaseCountsQuery = (params, enabled = true) => {
    return useQuery({
        queryKey: QUERY_KEYS.counts(params),
        queryFn: () => warehouseCaseApi.getWarehouseCaseCounts(params),
        refetchInterval: REFETCH_INTERVAL.list,
        enabled,
    });
};
/** 409 "mevcut talebe git" ve e-posta bağlantısı: talep aynada yoksa backend ShippingLot'tan alır. */
export const fetchWarehouseCase = (caseNumber) => warehouseCaseApi.getCase({ caseNumber });
/** Sipariş detayındaki giriş butonu: paketin talepleri (sayfalamasız). */
export const useWarehouseCasesByPackageQuery = (warehousePackageId, enabled = true) => {
    return useQuery({
        queryKey: QUERY_KEYS.byPackage(warehousePackageId ?? 0),
        queryFn: () => warehouseCaseApi.listCasesForPackage({
            warehousePackageId: warehousePackageId,
        }),
        enabled: enabled && !!warehousePackageId,
    });
};
/** Sohbet açıkken kısa aralıkla tazelenir; modal kapanınca `enabled` ile durur. */
export const useWarehouseCaseMessagesQuery = (params, enabled = true) => {
    return useQuery({
        queryKey: QUERY_KEYS.messages(params.caseNumber),
        queryFn: () => warehouseCaseApi.getMessages(params),
        refetchInterval: enabled ? REFETCH_INTERVAL.messages : false,
        enabled: enabled && !!params.caseNumber,
    });
};
/** Menü rozeti: layout seviyesinde, oturum boyunca döner. */
export const useWarehouseCaseUnreadCountQuery = (enabled = true) => {
    return useQuery({
        queryKey: QUERY_KEYS.unreadCount(),
        queryFn: () => warehouseCaseApi.getUnreadCount(),
        refetchInterval: REFETCH_INTERVAL.unreadCount,
        enabled,
    });
};
export const useCreateWarehouseCaseMutation = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (params) => warehouseCaseApi.createCase(params),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: QUERY_KEYS.all });
        },
    });
};
export const useAddWarehouseCaseMessageMutation = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (params) => warehouseCaseApi.addMessage(params),
        onSuccess: (_data, params) => {
            // Thread anında güncellensin; liste satırındaki özet de değişti.
            queryClient.invalidateQueries({
                queryKey: QUERY_KEYS.messages(params.caseNumber),
            });
            queryClient.invalidateQueries({ queryKey: QUERY_KEYS.all });
        },
    });
};
/** Kapalı talebi yeniden açar; liste, sayaçlar ve sohbet tazelenir. */
export const useReopenWarehouseCaseMutation = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (caseNumber) => warehouseCaseApi.reopenWarehouseCase({ caseNumber }),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: QUERY_KEYS.all });
        },
    });
};
/**
 * Depodan gelen ekin indirme bağlantısı. ShippingLot bağlantıyı 15 dk geçerli verdiği için her
 * tıklamada yeniden istenir, cache'lenmez.
 */
export const fetchCaseMessageAttachmentLink = (caseNumber, attachmentId) => warehouseCaseApi.getCaseMessageAttachmentLink({ caseNumber, attachmentId });
const authHeaders = () => {
    const token = localStorage.getItem('jwt_token');
    if (!token) {
        return {};
    }
    return { Authorization: `Bearer ${token}` };
};
const parseAttachment = async (response) => {
    if (response.status === 401) {
        localStorage.removeItem('jwt_token');
        window.location.href = '/login';
    }
    if (!response.ok) {
        throw { response };
    }
    return (await response.json());
};
/** Tek dosya. Sunucu tür ve 10 MB sınırını da doğrular. */
export const uploadCaseAttachment = async (caseNumber, file) => {
    const body = new FormData();
    body.append('file', file);
    const response = await fetch(`/api/warehouse-cases/${encodeURIComponent(caseNumber)}/attachments`, { method: 'POST', headers: authHeaders(), body });
    return parseAttachment(response);
};
export const deleteCaseAttachment = async (caseNumber, attachmentId) => {
    const response = await fetch(`/api/warehouse-cases/${encodeURIComponent(caseNumber)}/attachments/${attachmentId}`, { method: 'DELETE', headers: authHeaders() });
    if (response.status === 401) {
        localStorage.removeItem('jwt_token');
        window.location.href = '/login';
    }
    if (!response.ok && response.status !== 204) {
        throw { response };
    }
};
export const useUploadCaseAttachmentMutation = () => {
    return useMutation({
        mutationFn: (params) => uploadCaseAttachment(params.caseNumber, params.file),
    });
};
export const useDeleteCaseAttachmentMutation = () => {
    return useMutation({
        mutationFn: (params) => deleteCaseAttachment(params.caseNumber, params.attachmentId),
    });
};
/** Sohbet açıldığında çağrılır; rozet sıfırlanır. */
export const useMarkWarehouseCaseReadMutation = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (caseNumber) => warehouseCaseApi.markRead({ caseNumber }),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: QUERY_KEYS.all });
        },
    });
};
